import { DeclineReasonConfig } from "@/apps/tatar/invoiceConfigApp/RACInterfaces";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import FormStruct from "@/components/Form/FormStruct/FormStruct";
import { FV } from "@/components/Form/Validation/FormValidators";
import ModalManager from "@/components/ModalComponent/ModalManager";
import WrappedText from "@/components/WrappedText/WrappedText";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import { ObjectId } from "@/model/db/General";
import BFAssignment, {
  Assignment,
} from "@/modules/abstract-ui/forms/assignment/BFAssignment";
import BFChooserPage from "@/modules/abstract-ui/forms/chooser/BFChooserPage";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BfIcon from "@/modules/abstract-ui/icon/BfIcon";
import { DefaultIcons } from "@/modules/abstract-ui/icon/DefaultIcons";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import CacheService from "@/services/CacheService";
import DataBusDefaults from "@/services/DataBusDefaults";
import LanguageService from "@/services/LanguageService";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import InvoiceService from "../../InvoiceService";
import { InvoiceDirection, InvoiceKind } from "../../RAInterfaces";
import "./RADeclineInvoiceForm.scss";

export type DeclineFormValue = {
  reason: string;
  reasonTodos: { type: string; value: string; id?: ObjectId }[];
  manualTodos: { type: string; value: string; id?: ObjectId }[];
  assignment: Assignment[];
  comment: string;
};
interface RAInvoiceDeclineProps {
  initialComment?: string;
  onClose: () => void;
  selected?: boolean;
  invoiceId: string;
  task?: boolean;
  onFinish?: () => void;
  showProcess?: boolean;

  direction: InvoiceDirection;
  kind: InvoiceKind;
  type: string;
}
const RADeclineInvoiceForm = (props: RAInvoiceDeclineProps) => {
  const [initial] = useState<DeclineFormValue>({
    assignment: [],
    reasonTodos: [],
    manualTodos: [],
    reason: null,
    comment: props.initialComment || "",
  });
  return (
    <FormStruct
      className={`ra-invoice-decline`}
      onSubmit={async (values) => {
        const doDecline = async (forceDecline: boolean) => {
          await InvoiceService.declineInvoice(
            props.invoiceId,
            {
              comment: values.comment,
              reasonId: values.reason,
              assigneeUsers: values.assignment.map((e) => e.id),
              todo: [
                ...(values.reasonTodos || []).map((e) => ({
                  id: e.id || null,
                  value: e.value,
                })),
                ...(values.manualTodos || []).map((e) => ({
                  id: e.id || null,
                  value: e.value,
                })),
              ],
              force: forceDecline,
            },
            false
          );
        };
        try {
          await doDecline(false);
          CacheService.getData({
            oType: "asset",
            id: props.invoiceId,
            assetType: AssetTypes.Invoicing.Invoice,
            forceReload: true,
            silentReload: true,
          });
          props.onFinish?.();
        } catch (err) {
          if (err.response?.status === 300) {
            ModalManager.confirm({
              message: i18n.t(
                "ra:DeclineInvoice.declineWhileInPayment",
                "Die Rechnung befindet sich gerade in Zahlung. Mit Ablehnen der Rechnung wird die Zahlung abgebrochen. Wollen Sie die Rechnung ablehnen?"
              ),
              confirmButtonText: i18n.t(
                "ra:DeclineInvoice.forceDecline",
                "Rechnung ablehnen"
              ),
              onConfirm: async () => {
                try {
                  await doDecline(true);
                  props.onFinish?.();
                } catch (err) {
                  DataBusDefaults.toast({
                    type: "error",
                    text: i18n.t("ra:DeclineInvoice.errorSubmit"),
                  });
                }
              },
              onAbort: () => {},
            });
          } else {
            DataBusDefaults.toast({
              type: "error",
              text: i18n.t("ra:DeclineInvoice.errorSubmit"),
            });
          }
        }
      }}
      initialValues={initial}
      hideActions
      notModal
      render={(formProps) => (
        <Field name="reason" validate={FV.compose(FV.required())}>
          {({ input, meta }) => (
            <BFChooserPage
              onBack={props.onClose}
              title={i18n.t("ra:DeclineInvoice.ReasonTitle", "Ablehnungsgrund")}
              description={i18n.t(
                "ra:DeclineInvoice.description",
                "Aus welchem Grund wollen Sie den Beleg ablehnen?"
              )}
              options={InvoiceStruct.getDeclineReasonsForChooser(
                props.direction,
                [props.type]
              )}
              {...input}
              {...FV.getValidation(meta)}
              onChange={(value: string) => {
                if (value) {
                  const reason = InvoiceStruct.getDeclineReason(
                    props.direction,
                    value
                  );

                  const assignments =
                    reason?.assignment
                      ?.map((e) =>
                        e.type !== "previousAssignee"
                          ? e.id.map((a) => ({
                              type: e.type,
                              id: a,
                            }))
                          : []
                      )
                      .flat() || [];
                  if (
                    reason.assignment.find((e) => e.type === "previousAssignee")
                  ) {
                    // get previous assignees
                  }

                  const reasonTodos =
                    reason?.todos?.map((e) => {
                      return {
                        id: e.id,
                        type: e.type,
                        value: e.value,
                      };
                    }) || [];
                  formProps.form.change("assignment", assignments);
                  formProps.form.change("manualTodos", []);
                  formProps.form.change("reasonTodos", reasonTodos);
                  input.onChange(value);
                } else {
                  input.onChange(null);
                }
              }}
              renderPage={(reason: string, goBack) => (
                <RADeclineInvoiceSecondPage
                  {...props}
                  reason={reason}
                  goBack={goBack}
                />
              )}
            />
          )}
        </Field>
      )}
    />
  );
};

export default RADeclineInvoiceForm;

const RADeclineInvoiceSecondPage = (
  props: {
    reason: string;
    goBack: () => void;
  } & RAInvoiceDeclineProps
) => {
  const [reasonObj, setReasonObj] = useState<DeclineReasonConfig | undefined>(
    undefined
  );

  useEffect(() => {
    if (props.reason) {
      const obj = InvoiceStruct.getDeclineReason(props.direction, props.reason);
      if (obj) {
        setReasonObj(obj);
      }
    }
  }, [props.reason]);

  return (
    <div className={`ra-invoice-decline-second-page`}>
      <div className={classNames("form-header")}>
        <div className={`page-back`}>
          <BFButton appearance="link" onClick={props.goBack}>
            <BfIcon {...DefaultIcons.BACK} size={"xxs"} />
          </BFButton>
        </div>
        <div className={classNames("page-name __h3")}>
          {LanguageService.translateLabel(reasonObj?.displayName)}
        </div>
      </div>
      <div className={`scroll-container padding-20`}>
        <div className={`description  __sub-text`}>
          <WrappedText
            text={i18n.t(
              "ra:DeclineInvoice.description",
              "Bitte beachten Sie: Durch die Ablehnung wird automatisch ein Folgeauftrag erstellt, der anschließend den zuständigen Mitarbeitenden zur Bearbeitung zugewiesen wird.\nIm Rahmen dieses Prozesses werden die erforderlichen Aktionen systematisch erfasst und nachfolgend aufgelistet.\nBitte füllen Sie das folgende Formular vollständig und sachgerecht aus, um eine reibungslose Weiterbearbeitung sicherzustellen."
            )}
          />
        </div>

        <div className={`todo-list`}>
          <div
            className={`todo-list-header __h3 padding-top-20 padding-bottom-10`}
          >
            {i18n.t(
              "ra:DeclineInvoice.todoListHeader",
              "Erforderliche Aktionen im Folgeauftrag"
            )}
          </div>
          <FormFieldValues names={["reasonTodos"]}>
            {([reasonTodos]) => (
              <FieldArray name="manualTodos">
                {({ fields }) => (
                  <>
                    {reasonTodos?.length === 0 && fields.length === 0 ? (
                      <div className={`no-todos __sub-text`}>
                        {i18n.t(
                          "ra:DeclineInvoice.noActionsRequired",
                          "Es sind keine Aktionen erforderlich."
                        )}
                      </div>
                    ) : null}
                    <ul className={`todo-list-item`}>
                      {reasonTodos?.map((todo) => {
                        return (
                          <li
                            className={`todo-list-item-text`}
                            key={todo.type + "" + (todo.value || "")}
                          >
                            {todo.type === "manualTask"
                              ? todo.value
                              : i18n.t(
                                  `ra:Invoice.DeclineReasonActionType.${todo.type}Description`,
                                  todo.type
                                )}
                          </li>
                        );
                      })}
                      {fields.map((name, index) => (
                        <li key={index}>
                          <Field
                            name={`${name}.value`}
                            validate={FV.required()}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                placeholder={i18n.t(
                                  "ra:DeclineInvoice.manualTaskPlaceholder",
                                  "Was soll der Bearbeiter tun?"
                                )}
                                suffix={
                                  <BFButton
                                    appearance="link"
                                    noPadding
                                    onClick={() => fields.remove(index)}
                                  >
                                    <BfIcon
                                      {...DefaultIcons.CLOSE}
                                      size="xxs"
                                    />
                                  </BFButton>
                                }
                                {...input}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </Field>
                        </li>
                      ))}
                      <li>
                        <BFButton
                          appearance="link"
                          onClick={() => {
                            fields.push({
                              type: "manualTask",
                              value: "",
                            });
                          }}
                          noPadding
                        >
                          {i18n.t(
                            "ra:DeclineInvoice.addManualTask",
                            "Aktion hinzufügen"
                          )}
                        </BFButton>
                      </li>
                    </ul>
                  </>
                )}
              </FieldArray>
            )}
          </FormFieldValues>
        </div>

        <FormFieldValues names={["reasonTodos", "manualTodos"]}>
          {([reasonTodos, manualTodos]) =>
            (reasonTodos?.length > 0 || manualTodos?.length > 0) && (
              <div className={`assignment-list`}>
                <div
                  className={`assignment-list-header __h3 padding-top-20 padding-bottom-10`}
                >
                  {i18n.t(
                    "ra:DeclineInvoice.assignmentListHeader",
                    "Zuweisung der Aktionen"
                  )}
                </div>
                <div className={`assignment-list-item`}>
                  <Field name="assignment" validate={FV.required()}>
                    {({ input, meta }) => (
                      <BFAssignment
                        identifier={`bf-assignment-${props.invoiceId}}`}
                        {...FV.getValidation(meta)}
                        {...input}
                        assignmentType="user"
                        asOverlay
                        // label={`${i18n.t("ra:DeclineInvoice.assignment")}*`}
                      />
                    )}
                  </Field>
                </div>
              </div>
            )
          }
        </FormFieldValues>

        <div className={` __h3 padding-top-20 padding-bottom-10`}>
          {i18n.t(
            "ra:DeclineInvoice.commentHeader",
            "Kommentar fÜr den Bearbeiter"
          )}
        </div>
        <Field name="comment" validate={FV.required()}>
          {({ input, meta }) => (
            <BFInput
              placeholder={i18n.t(
                "ra:DeclineInvoice.commentPlaceholder",
                "Bitte geben Sie hier ein Kommentar für den Bearbeiter ein."
              )}
              autoResize
              type="textarea"
              {...FV.getValidation(meta)}
              {...input}
              // label={`${i18n.t("ra:DeclineInvoice.assignment")}*`}
            />
          )}
        </Field>
      </div>
      <div className={`form-footer`}>
        <BFButton appearance="outline" onClick={props.onClose}>
          {i18n.t("Global.Buttons.Cancel")}
        </BFButton>
        <BFButton appearance="primary" type="submit">
          {i18n.t("Global.Buttons.decline")}
        </BFButton>
      </div>
    </div>
  );
};
